/**
 * PrinterConfigApi.ts — CRUD persisté des imprimantes tenant + hardware configs
 * (Strangler lot 2 — invariant 4.B.2).
 *
 * Distinct de PrintingApi (orchestration d'impression runtime) : ici on gère
 * la CONFIGURATION persistée (tables tenant_printers et hardware_configs).
 *
 * Toutes les méthodes retournent Result<T, AppError>. Aucun import React.
 *
 * Invariants respectés :
 *   4.A.10 — tenant_id absent des inputs ; paramètre tenantId séparé pour
 *            le scoping (RLS reste actif). Les UPDATE/DELETE filtrent par
 *            tenant_id quand fourni — sans ce filtre, RLS bloque
 *            silencieusement (0 ligne) au lieu de remonter une erreur.
 *   4.B.4  — zéro any, schemas Zod stricts (clés inconnues strippées).
 *
 * Consumers (lot 2) : PrintersConfig, TogoImprimantes, TpeBluetoothManager,
 * QuickSetupTab, NetworkPrinterDialog, TenantHardwarePanel,
 * useHardwareConfigs, useTenantHardware.
 */

import { supabase } from '@/integrations/supabase/client';
import { ok, err } from '@/api/result';
import type { Result } from '@/api/result';
import { appErrorFromSupabase, validationError, notFoundError } from '@/api/errors';
import type { AppError } from '@/api/errors';
import type { Json, TablesInsert } from '@/integrations/supabase/types';
import {
  printerConfigInputSchema,
  updatePrinterConfigSchema,
  hardwareConfigInputSchema,
} from './config-types';
import type {
  TenantPrinterRow,
  HardwareConfigRow,
  PrinterConfigInput,
  UpdatePrinterConfigInput,
  HardwareConfigInput,
} from './config-types';

// Alias pour caster SafeParseReturnType après un guard !parsed.success qui n'étreint pas
// avec strictNullChecks:false — donne accès à parsed.error.issues sans `any`.
type FailedParse = { error: { issues: Array<{ path: PropertyKey[]; message: string }> } };

function firstIssueError(parsed: FailedParse): AppError {
  const firstIssue = parsed.error.issues[0];
  return validationError(
    firstIssue?.path.join('.') ?? 'unknown',
    firstIssue?.message ?? 'Données invalides',
  );
}

export const PrinterConfigApi = {
  /**
   * Liste les imprimantes configurées d'un tenant, défaut en premier puis
   * par nom (ordre historique PrintersConfig).
   */
  async listPrinters(tenantId: string): Promise<Result<TenantPrinterRow[], AppError>> {
    const { data, error } = await supabase
      .from('tenant_printers')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('is_default', { ascending: false })
      .order('name');

    if (error) {
      return err(appErrorFromSupabase(error, 'PrinterConfigApi.listPrinters'));
    }
    return ok(data ?? []);
  },

  /**
   * Crée une imprimante. tenantId optionnel : requis seulement en contexte
   * admin cross-tenant ou quand le DEFAULT serveur n'est pas disponible.
   */
  async createPrinter(
    input: PrinterConfigInput,
    tenantId?: string,
  ): Promise<Result<TenantPrinterRow, AppError>> {
    const parsed = printerConfigInputSchema.safeParse(input);
    if (!parsed.success) {
      return err(firstIssueError(parsed as typeof parsed & FailedParse));
    }

    const parsedData = parsed.data as PrinterConfigInput;
    const insertPayload = tenantId ? { ...parsedData, tenant_id: tenantId } : parsedData;
    // cast : Zod valide que name (required en DB) est présent au runtime.
    const { data, error } = await supabase
      .from('tenant_printers')
      .insert(insertPayload as TablesInsert<'tenant_printers'>)
      .select('*')
      .single();

    if (error) {
      return err(appErrorFromSupabase(error, 'PrinterConfigApi.createPrinter'));
    }
    if (!data) {
      return err(notFoundError('tenant_printer', 'new'));
    }
    return ok(data);
  },

  /**
   * Met à jour une imprimante. Le filtre tenant_id (quand fourni) évite le
   * blocage RLS silencieux constaté sur PrintersConfig.
   */
  async updatePrinter(
    id: string,
    input: UpdatePrinterConfigInput,
    tenantId?: string,
  ): Promise<Result<TenantPrinterRow, AppError>> {
    const parsed = updatePrinterConfigSchema.safeParse(input);
    if (!parsed.success) {
      return err(firstIssueError(parsed as typeof parsed & FailedParse));
    }

    const parsedData = parsed.data as UpdatePrinterConfigInput;
    let query = supabase.from('tenant_printers').update(parsedData).eq('id', id);
    if (tenantId !== undefined) {
      query = query.eq('tenant_id', tenantId);
    }
    const { data, error } = await query.select('*').single();

    if (error) {
      return err(appErrorFromSupabase(error, 'PrinterConfigApi.updatePrinter'));
    }
    if (!data) {
      return err(notFoundError('tenant_printer', id));
    }
    return ok(data);
  },

  /** Supprime une imprimante (config matérielle, pas une table fiscale). */
  async deletePrinter(id: string, tenantId?: string): Promise<Result<void, AppError>> {
    let query = supabase.from('tenant_printers').delete().eq('id', id);
    if (tenantId !== undefined) {
      query = query.eq('tenant_id', tenantId);
    }
    const { error } = await query;

    if (error) {
      return err(appErrorFromSupabase(error, 'PrinterConfigApi.deletePrinter'));
    }
    return ok(undefined);
  },

  /**
   * Désassocie toutes les imprimantes d'une caisse (register_id → null).
   * Utilisé avant suppression d'un register (QuickSetupTab admin).
   */
  async unlinkPrintersFromRegister(
    registerId: string,
    tenantId?: string,
  ): Promise<Result<void, AppError>> {
    let query = supabase
      .from('tenant_printers')
      .update({ register_id: null })
      .eq('register_id', registerId);
    if (tenantId !== undefined) {
      query = query.eq('tenant_id', tenantId);
    }
    const { error } = await query;

    if (error) {
      return err(appErrorFromSupabase(error, 'PrinterConfigApi.unlinkPrintersFromRegister'));
    }
    return ok(undefined);
  },

  /**
   * Agrégat cross-tenant (monitoring super_admin) : lignes (tenant_id,
   * is_active) des imprimantes des tenants demandés. RLS s'applique —
   * seul un rôle autorisé voit au-delà de son tenant.
   */
  async listPrintersForTenants(
    tenantIds: string[],
  ): Promise<Result<Array<{ tenant_id: string; is_active: boolean | null }>, AppError>> {
    const { data, error } = await supabase
      .from('tenant_printers')
      .select('tenant_id, is_active')
      .in('tenant_id', tenantIds);

    if (error) {
      return err(appErrorFromSupabase(error, 'PrinterConfigApi.listPrintersForTenants'));
    }
    return ok(data ?? []);
  },

  /**
   * Enregistre le résultat d'un test d'impression (last_test_at/status).
   * Le filtre tenant_id est requis pour que RLS autorise l'UPDATE.
   * Volontairement séparé d'updatePrinter : les champs last_test_* sont
   * de la télémétrie, pas de la configuration.
   */
  async recordPrinterTestResult(
    id: string,
    status: 'success' | 'error',
    tenantId?: string,
  ): Promise<Result<void, AppError>> {
    let query = supabase
      .from('tenant_printers')
      .update({ last_test_at: new Date().toISOString(), last_test_status: status })
      .eq('id', id);
    if (tenantId !== undefined) {
      query = query.eq('tenant_id', tenantId);
    }
    const { error } = await query;

    if (error) {
      return err(appErrorFromSupabase(error, 'PrinterConfigApi.recordPrinterTestResult'));
    }
    return ok(undefined);
  },

  /**
   * Retourne l'id de l'imprimante BT du tenant portant cette adresse MAC,
   * ou null. Sert l'upsert-par-MAC de TogoImprimantes (anti-doublon).
   */
  async findPrinterIdByBtMac(
    tenantId: string,
    btMacAddress: string,
  ): Promise<Result<string | null, AppError>> {
    const { data, error } = await supabase
      .from('tenant_printers')
      .select('id')
      .eq('tenant_id', tenantId)
      .eq('bt_mac_address', btMacAddress)
      .maybeSingle();

    if (error) {
      return err(appErrorFromSupabase(error, 'PrinterConfigApi.findPrinterIdByBtMac'));
    }
    return ok(data?.id ?? null);
  },

  /**
   * Liste les configs hardware, optionnellement filtrées par profil ou
   * tenant (panneaux admin). Tri created_at desc par défaut, device_type
   * asc pour les vues groupées par type.
   */
  async listHardwareConfigs(scope?: {
    profileId?: string;
    tenantId?: string;
    orderBy?: 'created_at_desc' | 'device_type_asc';
  }): Promise<Result<HardwareConfigRow[], AppError>> {
    let query = supabase.from('hardware_configs').select('*');
    if (scope?.profileId !== undefined) {
      query = query.eq('profile_id', scope.profileId);
    }
    if (scope?.tenantId !== undefined) {
      query = query.eq('tenant_id', scope.tenantId);
    }
    const { data, error } =
      scope?.orderBy === 'device_type_asc'
        ? await query.order('device_type', { ascending: true })
        : await query.order('created_at', { ascending: false });

    if (error) {
      return err(appErrorFromSupabase(error, 'PrinterConfigApi.listHardwareConfigs'));
    }
    return ok(data ?? []);
  },

  /**
   * Met à jour une config hardware. Le scope (profile_id ou tenant_id,
   * quand fourni) borne l'opération à la cible — sans lui, RLS bloque
   * silencieusement les panneaux admin cross-tenant.
   */
  async updateHardwareConfig(
    id: string,
    input: Partial<HardwareConfigInput>,
    scope?: { profileId?: string; tenantId?: string },
  ): Promise<Result<HardwareConfigRow, AppError>> {
    const parsed = hardwareConfigInputSchema.partial().safeParse(input);
    if (!parsed.success) {
      return err(firstIssueError(parsed as typeof parsed & FailedParse));
    }

    const parsedData = parsed.data as Omit<Partial<HardwareConfigInput>, 'config_data'> & {
      config_data?: { [key: string]: Json };
    };
    let query = supabase.from('hardware_configs').update(parsedData).eq('id', id);
    if (scope?.profileId !== undefined) {
      query = query.eq('profile_id', scope.profileId);
    }
    if (scope?.tenantId !== undefined) {
      query = query.eq('tenant_id', scope.tenantId);
    }
    const { data, error } = await query.select('*').single();

    if (error) {
      return err(appErrorFromSupabase(error, 'PrinterConfigApi.updateHardwareConfig'));
    }
    if (!data) {
      return err(notFoundError('hardware_config', id));
    }
    return ok(data);
  },

  /** Supprime une config hardware (config matérielle, pas une table fiscale). */
  async deleteHardwareConfig(
    id: string,
    scope?: { profileId?: string; tenantId?: string },
  ): Promise<Result<void, AppError>> {
    let query = supabase.from('hardware_configs').delete().eq('id', id);
    if (scope?.profileId !== undefined) {
      query = query.eq('profile_id', scope.profileId);
    }
    if (scope?.tenantId !== undefined) {
      query = query.eq('tenant_id', scope.tenantId);
    }
    const { error } = await query;

    if (error) {
      return err(appErrorFromSupabase(error, 'PrinterConfigApi.deleteHardwareConfig'));
    }
    return ok(undefined);
  },

  /**
   * Crée une config hardware (table hardware_configs, scope profil).
   * tenantId optionnel pour les panneaux admin cross-tenant.
   */
  async createHardwareConfig(
    input: HardwareConfigInput,
    tenantId?: string,
  ): Promise<Result<HardwareConfigRow, AppError>> {
    const parsed = hardwareConfigInputSchema.safeParse(input);
    if (!parsed.success) {
      return err(firstIssueError(parsed as typeof parsed & FailedParse));
    }

    const parsedData = parsed.data as Omit<HardwareConfigInput, 'config_data'> & {
      config_data?: { [key: string]: Json };
    };
    const insertPayload = tenantId ? { ...parsedData, tenant_id: tenantId } : parsedData;
    // cast : Zod valide que device_name (required en DB) est présent au runtime.
    const { data, error } = await supabase
      .from('hardware_configs')
      .insert(insertPayload as TablesInsert<'hardware_configs'>)
      .select('*')
      .single();

    if (error) {
      return err(appErrorFromSupabase(error, 'PrinterConfigApi.createHardwareConfig'));
    }
    if (!data) {
      return err(notFoundError('hardware_config', 'new'));
    }
    return ok(data);
  },
};
